import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersist from 'vuex-persist'

import { API_REQUEST } from '../api'
import { i18n } from '../boot/i18n'
import { getIndexedCollectionSimple, formDate, dateTimeToHoursMinutes } from '../common/helpers'
import { CustomError } from '../modules/error'
import { setQuasarInternalLanguage } from '../modules/quasarInternalLanguage'

Vue.use(Vuex)

let isFailedFetchTics = false

const vuexPersist = new VuexPersist({
  key: 'TA',
  storage: localStorage
})

function getClById (res) {
  const clById = {} // coming, leaving by worker_id
  for (let el of res) {
    if (el.is_verified && el.employee_id) {
      const keyName = `${el.user_id}_${el.employee_id}`
      if (!clById[keyName]) clById[keyName] = {}
      const curDt = formDate(el.dttm)
      if (clById[keyName][el.type]) {
        if (el.type === 'C' && (curDt < clById[keyName][el.type] || curDt > clById[keyName].L)) {
          clById[keyName][el.type] = curDt
        }
        if (el.type === 'L' && curDt > clById[keyName][el.type]) {
          clById[keyName][el.type] = curDt
        }
      } else {
        clById[keyName][el.type] = curDt
      }
      if (clById[keyName].L < clById[keyName].C) {
        delete clById[keyName].L
      }
    }
  }
  return clById
}

export const storeOptions = {
  state () {
    return {
      selectedWorker: null,
      shop: null,
      tickPoint: null,
      network: null,
      result: null,
      errorModal: {
        enabled: false,
        message: ''
      },
      firstPics: [],
      firstPicsPaused: true,
      tickDetails: {},
      comingLatenessShift: 0, // ms
      leavingDumpnessShift: 0, // ms
      style: {},
      locale: localStorage.getItem('locale') || 'ru-ru',
      completedShift: {},
      nextShift: {},
      tickType: '',
      workers: []
    }
  },

  mutations: {
    setSelectedWorker (state, payload) {
      const worker = {
        ...payload,
        employee: payload.employees
          .find(empl => empl.id === payload.employee_id)
      }
      delete worker.employees

      state.selectedWorker = worker
    },
    setShop (state, payload) {
      state.shop = payload
    },
    setNetwork (state, payload) {
      state.network = payload
    },
    setTickPoint (state, payload) {
      state.tickPoint = payload
    },
    setResult (state, payload) {
      state.result = Object.freeze(payload)
    },
    setErrorModal (state, payload) {
      if (payload.enabled === false) payload.message = ''
      state.errorModal = payload
    },
    setComingLeavingShifts (state, payload) {
      state.comingLatenessShift = payload.comingLatenessShift || 0
      state.leavingDumpnessShift = payload.leavingDumpnessShift || 0
    },
    addFirstPic (state, payload) {
      state.firstPics.push(Object.freeze(payload))
      if (state.firstPics.length > 3) state.firstPics.shift()
    },
    setFirstPicsPaused (state, payload) {
      state.firstPicsPaused = payload
    },
    setTickDetails (state, payload) {
      state.tickDetails = payload
    },
    setStyle (state, payload) {
      state.style = payload
    },
    setLocale (state, { value }) {
      state.locale = value
    },
    setCompletedShift (state, payload) {
      state.completedShift = payload
    },
    setTickType (state, type) {
      state.tickType = type
    },

    setWorkers (state, workers) {
      state.workers = workers
    },

    setIsFailedFetchTics (state, payload) {
      isFailedFetchTics = payload
    }
  },

  getters: {
    getFullName (state) {
      return (state.selectedWorker.last_name + ' ' +
    state.selectedWorker.first_name + ' ' +
    (state.selectedWorker.middle_name || '')).trim()
    }
  },

  actions: {
    changeLocale ({ commit }, locale) {
      i18n.locale = locale
      setQuasarInternalLanguage(locale)

      const cookieLocale = locale.toLowerCase().slice(0, 2)
      document.cookie = `django_language=${cookieLocale}`

      commit('setLocale', { value: locale })
    },

    async fetchWorkers ({ commit, state }, { isForce = false } = { isForce: false }) {
      if (!state.shop || !state.network) {
        CustomError.createAndThrow('Invalid shop data in store.', { httpStatus: 401 })
      }
      if (state.workers && state.workers.length && !isForce) {
        return
      }
      const workers = await API_REQUEST('getWorkerDays')
      const httpStatus = workers.http_status

      if (httpStatus !== 200) {
        CustomError.createAndThrow('fetchWorkers server request error', { httpStatus })
      }

      workers.reverse().map(el => {
        const firstName = el.first_name.toLowerCase()
        const lastName = el.last_name.toLowerCase()
        el.lastFirstName = `${firstName} ${lastName} ` // for searching
        el.firstLastName = `${lastName} ${firstName} `
        el.ticks = null
      })
      commit('setWorkers', workers)
    },

    async fetchTicks ({ commit, state }) {
      if (isFailedFetchTics) {
        return
      }
      try {
        const res = await API_REQUEST('getFilteredTicks')
        const clById = getClById(res)

        const workers = JSON.parse(JSON.stringify(state.workers))
        const workersIndexed = getIndexedCollectionSimple(workers, 'user_id')

        const completedShift = {}
        const timeZone = state.shop && state.shop.timezone

        Object.keys(clById).forEach(key => {
          let workerDayIndex = 0
          if (Object.keys(clById[key]).length === 2) {
            completedShift[key] = true
            workerDayIndex = 1
          }
          const [userId, emplId] = key.split('_')
          const workerToEdit = workersIndexed[userId]

          if (!workerToEdit) {
            return
          }

          const employeeIndex = workerToEdit.employees
            .findIndex(empl => String(empl.id) === String(emplId))

          const workerDay = workerToEdit.employees[employeeIndex].worker_days[workerDayIndex]

          const resObj = {}
          if (clById[key].C) {
            const workStartStr = workerDay && workerDay.dttm_work_start
            const time = clById[key].C // ms
            resObj.t1 = dateTimeToHoursMinutes(time, timeZone)
            if (workStartStr) {
              const workStart = new Date(workStartStr.replace('Z', '')).getTime()
              resObj.late = time > workStart - state.comingLatenessShift
            } else {
              resObj.late = false
            }
          }

          if (clById[key].L) {
            const workEndStr = workerDay && workerDay.dttm_work_end
            const time = clById[key].L // ms
            resObj.t2 = dateTimeToHoursMinutes(time, timeZone)
            if (workEndStr) {
              const workEnd = new Date(workEndStr.replace('Z', '')).getTime()
              resObj.dumped = time < workEnd + state.leavingDumpnessShift
            } else {
              resObj.dumped = false
            }
          }
          workerToEdit.ticks = resObj
        })
        commit('setWorkers', workers)
        commit('setCompletedShift', completedShift)
      } catch (err) {
        isFailedFetchTics = true
        throw err
      }
    }
  },

  plugins: [vuexPersist.plugin],
  // enable strict mode (adds overhead!)
  // for dev mode only
  strict: process.env.DEV
}

export default new Vuex.Store(storeOptions)
